import { createContext, useContext, useState, ReactNode } from 'react';

interface MemoEditorContextType {
    isOpen: boolean;
    mode: 'create' | 'edit' | 'follow-up';
    initialContent: string;
    memoId?: string;
    memoDate?: Date;
    memoStatus?: 'ACTIVE' | 'ARCHIVED';
    relatedMemoId?: string;
    openEditor: (options?: {
        mode?: 'create' | 'edit' | 'follow-up';
        content?: string;
        memoId?: string;
        date?: Date;
        status?: 'ACTIVE' | 'ARCHIVED';
        relatedMemoId?: string;
    }) => void;
    closeEditor: () => void;
}

const MemoEditorContext = createContext<MemoEditorContextType | undefined>(undefined);

export function MemoEditorProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState<'create' | 'edit' | 'follow-up'>('create');
    const [initialContent, setInitialContent] = useState('');
    const [memoId, setMemoId] = useState<string | undefined>(undefined);
    const [memoDate, setMemoDate] = useState<Date | undefined>(undefined);
    const [memoStatus, setMemoStatus] = useState<'ACTIVE' | 'ARCHIVED' | undefined>(undefined);
    const [relatedMemoId, setRelatedMemoId] = useState<string | undefined>(undefined);

    const openEditor = (options: {
        mode?: 'create' | 'edit' | 'follow-up';
        content?: string;
        memoId?: string;
        date?: Date;
        status?: 'ACTIVE' | 'ARCHIVED';
        relatedMemoId?: string;
    } = {}) => {
        setMode(options.mode || 'create');
        setInitialContent(options.content || '');
        setMemoId(options.memoId);
        setMemoDate(options.date);
        setMemoStatus(options.status);
        setRelatedMemoId(options.relatedMemoId);
        setIsOpen(true);
    };

    const closeEditor = () => {
        setIsOpen(false);
        // Reset state after closing (optional, but good for cleanup)
        setTimeout(() => {
            setMode('create');
            setInitialContent('');
            setMemoId(undefined);
            setMemoDate(undefined);
            setMemoStatus(undefined);
            setRelatedMemoId(undefined);
        }, 300);
    };

    return (
        <MemoEditorContext.Provider value={{
            isOpen,
            mode,
            initialContent,
            memoId,
            memoDate,
            memoStatus,
            relatedMemoId,
            openEditor,
            closeEditor
        }}>
            {children}
        </MemoEditorContext.Provider>
    );
}

export function useMemoEditor() {
    const context = useContext(MemoEditorContext);
    if (context === undefined) {
        throw new Error('useMemoEditor must be used within a MemoEditorProvider');
    }
    return context;
}
